/**
 * Belt-and-suspenders fallback for `inert` (not supported in Chrome <102,
 * Safari <15.5, Firefox <112 — e.g. older smart-TV browsers). Pair with
 * `inert` so keyboard users on legacy browsers don't tab into hidden content.
 */
export const FOCUSABLE_SELECTOR = [
  'a[href]',
  'area[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'iframe',
  'audio[controls]',
  'video[controls]',
  '[contenteditable]:not([contenteditable="false"])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/**
 * Sets `tabindex="-1"` on all focusable descendants of `root`, backing up any
 * pre-existing tabindex in `backupAttr` so it can be restored later.
 */
export const disableFocusableDescendants = (root: HTMLElement, backupAttr: string): void => {
  root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR).forEach(el => {
    if (el.hasAttribute(backupAttr)) return;
    el.setAttribute(backupAttr, el.getAttribute('tabindex') ?? '');
    el.setAttribute('tabindex', '-1');
  });
};

/**
 * Restores the original tabindex (or removes it) on descendants previously
 * disabled via `disableFocusableDescendants` with the same `backupAttr`.
 */
export const restoreFocusableDescendants = (root: HTMLElement, backupAttr: string): void => {
  root.querySelectorAll<HTMLElement>(`[${backupAttr}]`).forEach(el => {
    const orig = el.getAttribute(backupAttr);
    if (orig === '') {
      el.removeAttribute('tabindex');
    } else if (orig !== null) {
      el.setAttribute('tabindex', orig);
    }
    el.removeAttribute(backupAttr);
  });
};
